import {
  Box,
  CircularProgress,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { deleteAPICall, getAPICall, postAPICall } from "lib/callApi";
import { ASSIGN_KPI_TO_USER, FETCH_SINGLE_KPI_TEMPLATE, REMOVE_KPI } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { compnayUsersAction } from "redux/slice/companyUsers";
import Swal from "sweetalert2";
import AssignedToUsers from "./AssignedToUsers";
import EditTemplate from "./EditTemplate";
import KpiDetailsTable from "./KpiDetailsTable";

function TemplateDetailModal({
  isMobileDisplay,
  showDetailModal,
  handleDetailModalClose,
  alreadyAssignedUsers,
  id,
  setShowDetailModal,
  kpiActivities,
  getKpiActivities,
  // handleMakeCopy,
  inEditMode = false,
}) {
  const companyUsers = useSelector((state) => state.companyUsers.companyUsers.list);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [oldAssignedUsers, setOldAssignedUsers] = useState([]);
  const [isDataFetching, setIsDataFetching] = useState(true);
  const [data, setData] = useState({ activitiesWithPoints: [], userAssignedToTemplate: [] });
  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(inEditMode);
  const [canEdit, setCanEdit] = useState(false);
  // const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "90%" : 750,
    maxHeight: "80%",
    bgcolor: "#ffffff",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
    borderRadius: "0.5rem",
    outline: "none",
  };

  // const handleChange = (panel) => (event, newExpanded) => {
  //   setExpanded(newExpanded ? panel : false);
  // };

  const getSingleKpiTemplate = () => {
    getAPICall(`${FETCH_SINGLE_KPI_TEMPLATE}/${id}`, true)
      .then((res) => {
        setCanEdit(res.data.data.template.status !== "alysio_template");
        setData(res.data.data);
        setAssignedUsers([...res.data.data.userAssignedToTemplate]);
        setOldAssignedUsers([...res.data.data.userAssignedToTemplate]);
        setIsDataFetching(false);
      })
      .then(() => setIsDataFetching(false));
  };

  useEffect(() => {
    getSingleKpiTemplate();
    dispatch(compnayUsersAction.getCompanyUsersRequest());
  }, []);

  useEffect(() => {
    setData({ ...data, userAssignedToTemplate: assignedUsers });
  }, [assignedUsers]);

  const assignUserToTemplate = async (skipSwalPopup) => {
    const removedUsers = oldAssignedUsers
      ?.filter(
        (oldAssignedUser) =>
          !assignedUsers?.some((assignedUser) => assignedUser?.id === oldAssignedUser?.id)
      )
      ?.map((_) => _.id);
    const newAddedUsers = assignedUsers
      ?.filter(
        (assignedUser) =>
          !oldAssignedUsers?.some((oldAssignedUser) => assignedUser?.id === oldAssignedUser?.id)
      )
      ?.map((_) => _.id);
    await postAPICall(ASSIGN_KPI_TO_USER, true, { templateId: id, newAddedUsers, removedUsers })
      .then(() => {
        if (skipSwalPopup) return true;
        getSingleKpiTemplate();
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Data updated succesfully.",
          }),
          {
            variant: "custom",
          }
        );
        handleDetailModalClose(undefined, undefined, true);
        setIsDataFetching(false);
        return true;
      })
      .then(() => setIsDataFetching(false))
      .catch(() => {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not assign the template to user",
          }),
          {
            variant: "custom",
          }
        );
      })
      .finally(() => setIsRequestApiLoading(false));
  };

  const callDeleteTemplate = async (templateId) => {
    try {
      handleDetailModalClose();
      Swal.fire({
        title: `Are you sure you want to delete ${data.template?.name}?`,
        text: "All users who are assigned this template will no longer be assigned",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const responseResult = await deleteAPICall(REMOVE_KPI + templateId, true);

          if (responseResult?.data?.success) {
            handleDetailModalClose(undefined, undefined, true);
            enqueueSnackbar(
              JSON.stringify({
                icon: "info",
                title: "Information",
                text: `Template deleted successfully!`,
              }),
              {
                variant: "custom",
              }
            );
          }
        } else {
          setShowDetailModal(true);
          getSingleKpiTemplate();
        }
      });
    } catch (error) {
      handleDetailModalClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const openEditingModal = () => {
    setIsEditing(true);
  };

  const onSaveAssignedUsers = () => {
    setIsRequestApiLoading(true);
    assignUserToTemplate();
  };

  return (
    <Modal
      open={showDetailModal}
      onClose={handleDetailModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {isEditing ? (
        <Box sx={style}>
          <EditTemplate
            handleDetailModalClose={handleDetailModalClose}
            handleCancel={() => setIsEditing(false)}
            initialValues={{
              assignedUserId: assignedUsers,
              activitiesToBeDeleted: [],
              kpiActivities: data.activitiesWithPoints.map((_) => ({
                ..._,
                name: _?.KpiActivity?.name,
                id: _?.KpiActivity?.id,
                maxPointsPerDay: _.max_points_per_day,
              })) || [{ id: null, points: 1, name: "", count: 1, new: false }],
              name: data.template?.name || "",
              description: data.template?.description || "",
            }}
            id={data.template?.id}
            // templateFilter={templateFilter}
            getKpiActivities={getKpiActivities}
            companyUsers={companyUsers}
            alreadyAssignedUsers={alreadyAssignedUsers}
            kpiActivities={kpiActivities}
            getSingleKpiTemplate={getSingleKpiTemplate}
            setShowDetailModal={setShowDetailModal}
            assignedUsers={assignedUsers}
            setAssignedUsers={setAssignedUsers}
            oldAssignedUsers={oldAssignedUsers}
            assignUserToTemplate={assignUserToTemplate}
            isMobileDisplay={isMobileDisplay}
          />
        </Box>
      ) : (
        <Box sx={style}>
          {isDataFetching ? (
            <Stack alignItems="center">
              <CircularProgress
                sx={{
                  position: "fixed",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                }}
                color="info"
                size="10vh"
              />
            </Stack>
          ) : (
            ""
          )}
          <Box sx={{ mb: "1.5rem" }}>
            <Typography variant="h5" sx={{ mb: "0.5rem" }}>
              {data?.template?.name}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "400" }}>
              {data?.template?.description}
            </Typography>
          </Box>

          <AssignedToUsers
            assignedUsers={assignedUsers}
            setAssignedUsers={setAssignedUsers}
            alreadyAssignedUsers={alreadyAssignedUsers}
            oldAssignedUsers={oldAssignedUsers}
            handleDetailModalClose={handleDetailModalClose}
            isRequestApiLoading={isRequestApiLoading}
            onSaveAssignedUsers={onSaveAssignedUsers}
            showButtons={!isEditing}
          />

          <MDBox sx={{ marginTop: "1rem" }}>
            <KpiDetailsTable
              activitiesWithPoints={data?.activitiesWithPoints}
              isMobileDisplay={isMobileDisplay}
            />
            <Accordion
              sx={{
                border: "none",
                boxShadow: "none",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon />}
                sx={{
                  "& .Mui-expanded": { m: 0 },
                  "& :hover": {
                    color: "cornflowerblue",
                  },
                  minHeight: "32px !important",
                  mt: "0.5rem",
                }}
              >
                <Typography sx={{ color: "#344767" }}>Assigned User Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: 0 }}>
                  <Table aria-label="simple table">
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.userAssignedToTemplate?.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </MDBox>

          {canEdit && (
            <MDBox sx={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <MDButton
                variant="outlined"
                color="error"
                sx={{ marginTop: "1rem" }}
                onClick={() => {
                  callDeleteTemplate(data?.template.id);
                }}
              >
                Delete
              </MDButton>
              <MDButton
                color="secondaryBlue"
                sx={{ marginTop: "1rem" }}
                onClick={() => {
                  openEditingModal();
                }}
              >
                Edit
              </MDButton>
            </MDBox>
          )}
        </Box>
      )}
    </Modal>
  );
}

export default TemplateDetailModal;
